/**
 * Timeline — scene sequencer.
 *
 * Each scene runs through this lifecycle:
 *   1. ASCII reveal animation runs to completion (background)
 *   2. typographic text reveals in side / center bands
 *   3. ASCII sustained animations run continuously during dwell
 *   4. dwell — until the viewer advances OR dwellMs elapses
 *   5. fade typography + run ASCII departure animation
 *
 * Advancement model:
 *   The dwell phase is a race between a fallback timer and viewer input
 *   (click anywhere, Space, Enter, →, or ↓). Whichever fires first ends
 *   the scene. A discreet "click or press space" hint fades in once
 *   input is armed, ~600ms after typography is visible.
 *
 * Audio is intentionally omitted from this build — the catalog is
 * presented in silence (see hermes-skill/bestiary.skill.yaml).
 */

const FORWARD_KEYS = new Set([
  "Space",
  "Enter",
  "ArrowRight",
  "ArrowDown",
  "PageDown",
]);

const BACK_KEYS = new Set([
  "ArrowLeft",
  "ArrowUp",
  "PageUp",
  "Backspace",
]);

export class Timeline {
  constructor({ manifest, ascii, typographic }) {
    this.manifest = manifest;
    this.ascii = ascii;
    this.typographic = typographic;
    this.advanceHint = document.getElementById("advance-hint");
    this.curtain = document.getElementById("scene-curtain");
    this.mount = document.getElementById("ascii-mount");
    this.mobileHint = document.getElementById("mobile-tap-hint");
    this._mobileHintIdleTimer = null;
  }

  /**
   * Show/hide the painting layer. The painting only belongs in entry
   * scenes — preface and coda are pure-prose pages where any leftover
   * canvas pixels would bleed through the transparent prose overlay
   * and ghost the previous entry behind the text. Toggling opacity
   * (rather than display:none) keeps the layout stable on portrait,
   * where #ascii-mount is a flex item between the title and body
   * rows; collapsing it would shift the prose center band.
   */
  _setMountVisible(visible) {
    if (!this.mount) return;
    this.mount.classList.toggle("hidden", !visible);
  }

  /**
   * Show the mobile [tap] indicator. Static for 3 seconds, then a
   * gentle 1.6s sine pulse begins (per the .pulsing class). On any
   * user interaction (tap, scroll), call _resetMobileHintIdle() to
   * stop the pulse and restart the 3s idle timer. The element is
   * display:none on landscape, so these calls are no-ops on desktop.
   */
  _showMobileHint() {
    if (!this.mobileHint) return;
    this.mobileHint.classList.add("visible");
    this.mobileHint.classList.remove("pulsing");
    clearTimeout(this._mobileHintIdleTimer);
    this._mobileHintIdleTimer = setTimeout(() => {
      this.mobileHint.classList.add("pulsing");
    }, 3000);
  }

  _resetMobileHintIdle() {
    if (!this.mobileHint || !this.mobileHint.classList.contains("visible")) return;
    this.mobileHint.classList.remove("pulsing");
    clearTimeout(this._mobileHintIdleTimer);
    this._mobileHintIdleTimer = setTimeout(() => {
      this.mobileHint.classList.add("pulsing");
    }, 3000);
  }

  _hideMobileHint() {
    if (!this.mobileHint) return;
    this.mobileHint.classList.remove("visible", "pulsing");
    clearTimeout(this._mobileHintIdleTimer);
    this._mobileHintIdleTimer = null;
  }

  async play() {
    let i = 0;
    while (i < this.manifest.scenes.length) {
      let direction = "forward";
      try {
        direction = await this.playScene(this.manifest.scenes[i], i);
      } catch (e) {
        // A scene-level throw must NOT kill the whole timeline.
        console.warn("[timeline] scene", i, "threw; advancing:", e);
        direction = "forward";
      }
      // Move based on direction returned from playScene. "back" steps
      // to the previous scene; anything else (timeout or "forward")
      // advances. Backward at index 0 stays put rather than wrapping
      // to the coda.
      if (direction === "back" && i > 0) {
        i -= 1;
      } else {
        i += 1;
      }
    }
  }

  /**
   * Race a Promise against a timeout. Resolves either way — never
   * hangs the timeline. The watchdog catches any departure step that
   * silently never settles (a stuck rAF chain, an audio Promise that
   * the browser suspended, etc.).
   */
  _withWatchdog(promise, label, timeoutMs = 4000) {
    return Promise.race([
      promise,
      new Promise((resolve) =>
        setTimeout(() => {
          console.warn(`[timeline] ${label} watchdog fired after ${timeoutMs}ms`);
          resolve();
        }, timeoutMs),
      ),
    ]);
  }

  async playScene(scene, idx) {
    // Reset page scroll on every scene transition. On portrait
    // viewports the entry layout is a vertical stack (title →
    // painting → body) that scrolls the page; without this reset,
    // the next scene would start at whatever scroll position the
    // previous body text left behind, hiding the new title above
    // the fold. Hidden behind the scene curtain so the snap is
    // invisible. No-op on desktop where the layout doesn't scroll.
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }

    // Load ASCII source + animation script for entry-type scenes.
    // For non-entry scenes (preface, coda), hide the painting layer
    // outright AND clear the canvas — clear() alone has been observed
    // to leave a faint ghost of the previous entry visible through the
    // transparent prose overlay (suspect: a stray rAF tick repainting
    // before stopSustained takes effect, or a stale prev-grid cell that
    // diff-render skips). Hiding the mount is the belt-and-suspenders
    // guarantee that nothing from the previous entry can bleed through
    // behind preface or coda prose.
    if (scene.type === "entry") {
      this._setMountVisible(true);
      await this._withWatchdog(
        this.ascii.loadScene({
          asciiUrl: scene.ascii ?? null,
          animationScript: scene.animation ?? null,
        }),
        `loadScene(${scene.id})`,
        6000,
      );
      // Reveal first, sustained second. PaintingBreath writes every
      // painting cell on its first update() (with applied=false), so
      // starting it concurrently with the reveal means it overwrites
      // the noise on the first frame and the decode is invisible.
      // Deferring startSustained until the reveal completes keeps the
      // noise → painting transition clean.
      (async () => {
        try {
          await this.ascii.runReveal();
        } catch (e) {
          console.warn("[timeline] reveal threw, starting sustained anyway:", e);
        }
        this.ascii.startSustained();
      })();
    } else {
      this._setMountVisible(false);
      this.ascii.stopSustained();
      this.ascii.canvas.clear();
      this.ascii.canvas.render();
    }

    // Lift the scene curtain (if it's down from the previous scene's
    // departure). The fade-out overlaps the reveal — the painting
    // emerges from darkness as the noise resolves, which gives each
    // entry a theatrical "lights up" beat.
    this._curtainOut();

    // Typography reveal
    if (scene.text) {
      await this._withWatchdog(
        this.typographic.renderScene(scene.text, {
          revealMs: scene.revealMs ?? 0,
        }),
        `renderScene(${scene.id})`,
        4000,
      );
    }

    // Dwell — viewer can advance early. Returns "forward" (default,
    // also for timeout) or "back" if the viewer hit a back key.
    const dwellMs = scene.dwellMs ?? 12000;
    const isLast = idx === this.manifest.scenes.length - 1;
    const isFirst = idx === 0;
    const direction = await this._waitForAdvance(dwellMs, { isLast, isFirst });

    // Departure. The scene curtain is now the primary transition:
    // typography fades out + curtain drops to black, in parallel.
    // The previous ascii.runDeparture (FadeToNoise) was an artifact
    // from before the curtain existed — running both meant the
    // painting visibly dissolved into noise before the black, which
    // read as a glitch right before the cut. Curtain alone is cleaner.
    //
    // Each leg is wrapped in the watchdog so a single hung Promise
    // can't strand the timeline.
    const departures = [
      this._withWatchdog(
        this.typographic.fadeOut({ durationMs: 1000 }),
        `typo.fadeOut(${scene.id})`,
        2500,
      ),
    ];
    if (!isLast && scene.type === "entry") {
      departures.push(
        this._withWatchdog(
          this._curtainIn(900),
          `curtainIn(${scene.id})`,
          2000,
        ),
      );
    }
    if (!isLast) {
      await Promise.all(departures);
      // Stop sustained effects on the (now-hidden) outgoing painting
      // and clear the canvas behind the curtain so the next scene's
      // loadScene paints onto a clean slate.
      if (scene.type === "entry") {
        this.ascii.stopSustained();
        this.ascii.canvas.clear();
        this.ascii.canvas.render();
      }
      // Brief beat of darkness before the next scene loads + reveals.
      await new Promise((r) => setTimeout(r, 250));
    }
    return direction;
  }

  /**
   * Bring the scene curtain to full black. Resolves after the
   * fade-in duration so the caller can hold the beat.
   */
  async _curtainIn(durationMs = 500) {
    if (!this.curtain) return;
    this.curtain.style.transition = `opacity ${durationMs}ms ease`;
    this.curtain.classList.add("visible");
    await new Promise((r) => setTimeout(r, durationMs));
  }

  /**
   * Lift the scene curtain. Fire-and-forget — the fade overlaps the
   * next scene's reveal so the painting emerges from darkness.
   */
  _curtainOut(durationMs = 800) {
    if (!this.curtain) return;
    this.curtain.style.transition = `opacity ${durationMs}ms ease`;
    this.curtain.classList.remove("visible");
  }

  /**
   * Wait until either the dwell timer fires or the viewer advances.
   *
   * Input is "armed" after armDelayMs to prevent the splash-button
   * click from immediately blowing through scene 1, and to give the
   * eye a moment to settle on the painting before the hint appears.
   */
  _waitForAdvance(dwellMs, { isLast = false, isFirst = false, armDelayMs = 600 } = {}) {
    return new Promise((resolve) => {
      let done = false;
      let armed = false;

      const finish = (direction = "forward") => {
        if (done) return;
        done = true;
        document.removeEventListener("click", onClick);
        document.removeEventListener("keydown", onKey);
        window.removeEventListener("scroll", onIdleResetEvent);
        window.removeEventListener("touchstart", onIdleResetEvent);
        clearTimeout(armTimer);
        clearTimeout(fallbackTimer);
        this._hideHint();
        // Hide mobile hint instantly on advance — it'll re-appear
        // when the next scene's _waitForAdvance arms.
        this._hideMobileHint();
        resolve(direction);
      };

      const tryAdvance = (direction = "forward") => {
        if (!armed) return;
        // Backward navigation at the very first scene is a no-op —
        // ignore the keypress rather than confusing the user with
        // a wrap or an audible "pop".
        if (direction === "back" && isFirst) return;
        finish(direction);
      };

      const onClick = (e) => {
        if (e.target.closest("a, button")) return;
        tryAdvance("forward");
      };

      const onKey = (e) => {
        if (FORWARD_KEYS.has(e.code)) {
          e.preventDefault();
          tryAdvance("forward");
        } else if (BACK_KEYS.has(e.code)) {
          e.preventDefault();
          tryAdvance("back");
        }
      };

      // Touch/scroll without an advance keeps the user "active" — reset
      // the mobile hint's idle timer so the soft pulse only starts after
      // a true idle period. (Desktop wheel-scroll fires this too; harmless
      // since the mobile hint is display:none there.)
      const onIdleResetEvent = () => {
        if (armed) this._resetMobileHintIdle();
      };

      document.addEventListener("click", onClick);
      document.addEventListener("keydown", onKey);
      window.addEventListener("scroll", onIdleResetEvent, { passive: true });
      window.addEventListener("touchstart", onIdleResetEvent, { passive: true });

      const armTimer = setTimeout(() => {
        armed = true;
        // Show hints on every interactive scene including the coda —
        // it's the LAST manifest scene but still requires the user to
        // tap/space to advance to runClosingSequence (which is a
        // separate path in main.js, outside the timeline loop, and
        // doesn't go through _waitForAdvance — so it won't surface
        // the hint there). The earlier `if (!isLast)` guard meant
        // the coda silently waited with no nav cue, leaving the user
        // stranded on the postscriptum until they guessed to tap.
        this._showHint();
        this._showMobileHint();
      }, armDelayMs);

      const fallbackTimer = setTimeout(finish, dwellMs);
    });
  }

  _showHint() {
    if (!this.advanceHint) return;
    this.advanceHint.classList.add("visible");
  }

  _hideHint() {
    if (!this.advanceHint) return;
    this.advanceHint.classList.remove("visible");
  }
}
